import { useState } from 'react';
import { Building2, ChevronDown, ChevronUp, Home as HomeIcon, Slash } from 'lucide-react';

import { cn, formatTime } from '@/lib/utils';

type DeclaredLocation = 'OFFICE' | 'REMOTE';
type PunchType = 'IN' | 'OUT';

export interface VoidedPunch {
  id: string;
  type: PunchType;
  eventAt: string;
  declaredLocation: DeclaredLocation;
  source: string;
  voidedReason: string | null;
  voidedByPunchId: string | null;
  correctionRequest: {
    id: string;
    decidedAt: string | null;
    approver: { id: string; fullName: string } | null;
  } | null;
}

/**
 * Sección plegable con los fichajes anulados de una jornada (correcciones
 * aplicadas). Se muestra debajo del listado de fichajes activos en el
 * detalle del día. No aparece si no hay anulados.
 */
export function VoidedPunchesSection({ items }: { items: VoidedPunch[] }) {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;

  return (
    <div className="mt-2 border-t pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        aria-expanded={open}
      >
        <Slash className="h-3.5 w-3.5" />
        Mostrar fichajes anulados ({items.length})
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>

      {open ? (
        <ul className="divide-y border rounded bg-background mt-2">
          {items.map((p) => (
            <li key={p.id} className="px-3 py-1.5 text-xs text-muted-foreground">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'inline-flex items-center justify-center h-5 px-2 rounded-full text-[10px] font-medium line-through',
                      'bg-muted text-muted-foreground',
                    )}
                  >
                    {p.type}
                  </span>
                  <span className="tabular-nums line-through">{formatTime(p.eventAt)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {p.declaredLocation === 'OFFICE' ? (
                    <Building2 className="h-3 w-3" />
                  ) : (
                    <HomeIcon className="h-3 w-3" />
                  )}
                  <span>{p.declaredLocation === 'OFFICE' ? 'Oficina' : 'Remoto'}</span>
                </div>
              </div>
              {p.voidedReason ? (
                <p className="mt-0.5 italic">{p.voidedReason}</p>
              ) : null}
              {p.correctionRequest?.approver ? (
                <p className="mt-0.5 text-[10px]">
                  Aplicado por {p.correctionRequest.approver.fullName}
                  {p.correctionRequest.decidedAt
                    ? ` · ${new Date(p.correctionRequest.decidedAt).toLocaleString('es-ES', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}`
                    : ''}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
